import type { CreditSummary } from '@ni/shared';

/**
 * **Where an enrolled account's credit stands** (ruled 2026-09-26), from its
 * own credit read. Low is "cannot cover a dossier" at its current price; out is
 * "cannot cover any priced work" at the prices the office would press now (a
 * quick look within the month's included ones is free, so it does not count).
 * Held credits are not counted: they are committed to work in progress and come
 * back by themselves if it does not deliver.
 *
 * `none` is every account this says nothing about: not enrolled, and a plan
 * without paid work (Network only), whose presses are refused for the plan
 * whatever the balance.
 */
export type CreditStanding =
  | { state: 'none' }
  | { state: 'healthy' }
  | { state: 'low'; available: number; dossier: number; unitsPerCredit: number }
  | { state: 'out'; quickLooksLeft: boolean };

export function creditStanding(summary: CreditSummary): CreditStanding {
  if (!summary.charging_enabled || !summary.plan.paid_work) return { state: 'none' };
  const available = summary.balance.available;
  const paid = Object.values(summary.prices).filter(
    (units): units is number => units !== null && units > 0,
  );
  if (paid.length && available < Math.min(...paid))
    return {
      state: 'out',
      quickLooksLeft: summary.quick_looks.used < summary.quick_looks.included,
    };
  const dossier = summary.prices.person_research;
  if (dossier !== null && available < dossier)
    return { state: 'low', available, dossier, unitsPerCredit: summary.units_per_credit };
  return { state: 'healthy' };
}
